import {ChangeDetectionStrategy, Component, ElementRef, HostListener, Inject, OnInit, computed, effect, inject, signal, viewChild} from '@angular/core';
import copy from 'copy-to-clipboard';
import {ShiftHttpService, CalendarSubscription} from '../../_services/shift-http.service';
import {GlobalAlertService} from '../../../../_services/global-alert.service';
import {AlertLevel} from '../../../../enums/alert-level';

/** Shown while `scope` is not yet known (loading, or the initial load failed) — the pre-#685 wording. */
const DEFAULT_HELPER_TEXT = 'Abonnieren Sie Ihre Schichten in Ihrem Kalender.';

/** `scope: 'account'` — the merged feed over every active employer of the signed-in account. */
const ACCOUNT_HELPER_TEXT =
  'Der Kalenderlink enthält Ihre Schichten und Verfügbarkeiten bei allen Arbeitgebern, bei denen Sie ' +
  'beschäftigt sind.';

/** `scope: 'employee'` — the legacy per-employer link (password-only login, no account). */
const EMPLOYEE_HELPER_TEXT = 'Abonnieren Sie Ihre Schichten bei diesem Arbeitgeber in Ihrem Kalender.';

/**
 * "Kalenderabo" settings tile (#685). Hands out the employee panel's calendar subscription link and lets the
 * holder reset it.
 *
 * ### Two link scopes, one tile
 * `ShiftHttpService.getCalendarSubscription()` is loaded once on init purely to learn `scope()` — the account
 * link (merged across every active employer) vs. the legacy per-employer link — so the helper text under the
 * heading is accurate before the reader ever clicks anything. The reset button is shown for BOTH scopes: an
 * account holder resets the merged link, a password-only employee resets their single-employer link. Clicking
 * "Link kopieren" re-reads the subscription (idempotent — the server returns the same link until it is reset)
 * rather than reusing what `ngOnInit` fetched, so a link that changed in another tab is never copied stale.
 *
 * ### Reset is destructive, not irreversible
 * A reset immediately invalidates the OLD link (every calendar app still holding it starts getting 401s), so
 * it goes through the "Destructive-confirmation dialog" pattern from the style guide — a modal naming the
 * consequence, Cancel focused by default, Escape/backdrop both cancel — rather than the stronger
 * typed-confirmation dialog: the action is repairable (the new link keeps working, and nothing about the
 * account is destroyed), just something the reader must go re-do in every calendar app.
 */
@Component({
  selector: 'app-calendar-subscription',
  standalone: true,
  imports: [],
  templateUrl: './calendar-subscription.component.html',
  styleUrl: './calendar-subscription.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalendarSubscriptionComponent implements OnInit {
  private readonly shiftHttp = inject(ShiftHttpService);
  private readonly globalAlert = inject(GlobalAlertService);

  readonly loading = signal(true);
  readonly loadFailed = signal(false);
  readonly subscription = signal<CalendarSubscription | null>(null);

  readonly resetConfirmOpen = signal(false);
  readonly resetBusy = signal(false);

  private readonly cancelButton = viewChild<ElementRef<HTMLButtonElement>>('cancelButton');

  readonly scope = computed(() => this.subscription()?.scope ?? null);

  readonly helperText = computed(() => {
    switch (this.scope()) {
      case 'account':
        return ACCOUNT_HELPER_TEXT;
      case 'employee':
        return EMPLOYEE_HELPER_TEXT;
      default:
        return DEFAULT_HELPER_TEXT;
    }
  });

  constructor(@Inject('BASE_URL') private readonly baseUrl: string) {
    // Put the caret on Cancel as soon as the confirm dialog opens — the safe default for a destructive prompt.
    effect(() => {
      if (!this.resetConfirmOpen()) return;
      this.cancelButton()?.nativeElement.focus();
    });
  }

  ngOnInit(): void {
    this.load();
  }

  /** (Re-)reads the current subscription, purely to learn `scope()` for the helper text below the heading. */
  load(): void {
    this.loading.set(true);
    this.loadFailed.set(false);
    this.shiftHttp.getCalendarSubscription().subscribe({
      next: sub => {
        this.subscription.set(sub);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.loadFailed.set(true);
      }
    });
  }

  copyLink(): void {
    this.shiftHttp.getCalendarSubscription().subscribe({
      next: sub => {
        this.subscription.set(sub);
        this.loadFailed.set(false);
        this.copyToClipboard(this.linkFor(sub));
        this.globalAlert.createAndShow('Hinweis',
          'Der Kalenderlink wurde in die Zwischenablage kopiert, bitte vertraulich behandeln.', AlertLevel.info);
      },
      error: error => {
        console.error(error);
        this.globalAlert.createAndShow('Fehler', 'Beim Abrufen des Kalenders ist ein Fehler aufgetreten.',
          AlertLevel.error);
      }
    });
  }

  openResetConfirm(): void {
    this.resetConfirmOpen.set(true);
  }

  cancelReset(): void {
    this.resetConfirmOpen.set(false);
  }

  confirmReset(): void {
    if (this.resetBusy()) return;
    this.resetBusy.set(true);
    this.shiftHttp.resetCalendarSubscription().subscribe({
      next: sub => {
        this.resetBusy.set(false);
        this.resetConfirmOpen.set(false);
        this.subscription.set(sub);
        this.loadFailed.set(false);
        this.copyToClipboard(this.linkFor(sub));
        this.globalAlert.createAndShow('Kalenderlink zurückgesetzt',
          'Der neue Kalenderlink wurde in die Zwischenablage kopiert, bitte vertraulich behandeln. Bitte ' +
          'abonnieren Sie den Kalender in jeder Kalender-App, in der Sie ihn eingerichtet haben, erneut mit ' +
          'diesem Link.', AlertLevel.success);
      },
      error: error => {
        console.error(error);
        this.resetBusy.set(false);
        this.globalAlert.createAndShow('Fehler', 'Der Kalenderlink konnte nicht zurückgesetzt werden.',
          AlertLevel.error);
      }
    });
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.resetConfirmOpen()) this.cancelReset();
  }

  private linkFor(sub: CalendarSubscription): string {
    return `${this.baseUrl}/ep/shift/calendar${sub.path}`;
  }

  /**
   * The one seam a spec can spy on. `copy-to-clipboard`'s default export is a plain library function, not a
   * component method — wrapping the call here is what makes "the right URL was copied" assertable without
   * touching the real clipboard/`execCommand` in Karma.
   */
  protected copyToClipboard(link: string): void {
    copy(link);
  }
}
